import { uniq, without } from "lodash";

import { AutoAdjustDateLevel } from "../../../../../constants/autoAdjustDateLevel";
import { calendarSelectors } from "../../../../definition/calendar";
import { typeSelectors } from "../../../../definition/types";
import adjustItemsForChildDates from "./adjustItemsForChildDates";
import closestValidDateForItem from "./closestValidDateForItem";
import getConnectedItemsWithExactConstraint from "./getConnectedItemsWithExactConstraint";
import getConnectedItemsWithLooseConstraint from "./getConnectedItemsWithLooseConstraint";
import { moveItemAndDescendants } from "./moveItemAndDescendants";

/**
 *
 * @param {Map} inItemsById
 * @param {TimelineRecord} previousGlobalState
 * @param {array} checkIds
 * @param {boolean} recalculateCheckIds
 * @param {number} autoAdjustLevel
 * @param {DateRecord} backwardsLimitDate
 */
export const repairAndMoveAffectedItems = (
  inItemsById,
  previousGlobalState,
  checkIds,
  recalculateCheckIds,
  autoAdjustLevel,
  backwardsLimitDate
) => {
  const calendar = calendarSelectors.getCalendar(previousGlobalState);
  const typesById = typeSelectors.getTypesById(previousGlobalState);
  // The live state we will update as we proceed
  let itemsById = inItemsById;
  let originalIds = [...checkIds];
  let fixedIds = [];
  let looseIds = [];
  let lockedIds = [];
  let movedIds = [];

  while (originalIds.length || fixedIds.length || looseIds.length) {
    // In order, we process:
    // 1) Originally dragged items
    //       -- in one batch, just to repair them and calculate any relevant dependencies
    // 2) Fixed connections to anything moved so far
    // 3) Loose connections to anything moved so far
    // * This means that if we empty originalIds and fixedIds, we will start working on
    // * looseIds. But if one of those has a fixed connection, that gets shot to the top
    // * of the list ahead of other loose connections we have already gathered.
    const isOriginal = originalIds.length > 0;
    const checkOnly = originalIds.length > 0 && !recalculateCheckIds;
    // If this comes from the original list, then we don't need to move it
    // But we will still need to correct its child dates (and same for supers)
    let movedIdsThisLoop = [];
    if (checkOnly) {
      // Lock all IDs so we never come back to them
      movedIdsThisLoop = [...originalIds];
      lockedIds.push(...originalIds);
      // Clear the list since we will handle them all at once
      originalIds = [];
    } else {
      // Otherwise, we need to work out what we will process...
      const processId = originalIds.length
        ? originalIds.shift()
        : fixedIds.length
          ? fixedIds.shift()
          : looseIds.shift();

      // If p is not locked
      if (lockedIds.indexOf(processId) >= 0) {
        continue;
      }
      // Work out what date this item should be moved to
      // If it is original, then the correct date was already passed in
      // Otherwise, we need to calculate the date from dependencies or dateConstraints that have already moved
      const newDate = closestValidDateForItem(
        itemsById,
        previousGlobalState,
        processId,
        isOriginal ? null : movedIds,
        backwardsLimitDate
      );
      if (newDate) {
        // Move the item, and if required, its children
        const moveResult = moveItemAndDescendants(
          itemsById,
          processId,
          newDate,
          null,
          calendar,
          autoAdjustLevel,
          false
        );
        itemsById = moveResult.itemsById;
        movedIdsThisLoop = moveResult.movedIds;
      }
      // Add to the global list of exclusions
      lockedIds.push(processId);
    }

    // Now we need to repair these items' child dates, and the same for their supers
    const adjustResult = adjustItemsForChildDates(itemsById, movedIdsThisLoop, calendar, typesById);

    itemsById = adjustResult.itemsById;
    movedIdsThisLoop = adjustResult.movedIds;

    // Add them to our overall list of items we have moved before...
    movedIds.push(...movedIdsThisLoop);

    if (autoAdjustLevel >= AutoAdjustDateLevel.ResolveConstraints) {
      // Find any fixed date constraint items with a strict connection to the moved items
      fixedIds.push(
        ...getConnectedItemsWithExactConstraint(itemsById, previousGlobalState, movedIdsThisLoop, lockedIds)
      );

      // And any with a loose connection
      looseIds.push(
        ...getConnectedItemsWithLooseConstraint(itemsById, previousGlobalState, movedIdsThisLoop, lockedIds)
      );
    }

    // A little bit of cleanup... ensure fixedIds are unique, and looseIds are unique to both loose and fixed lists
    fixedIds = uniq(fixedIds);
    looseIds = without(uniq(looseIds), ...fixedIds);
  }

  return itemsById;
};
